import React from 'react';
import {
  Search,
  Plus,
  Filter,
  MoreHorizontal,
  Calendar,
  User,
  Clock,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';
import { Task } from '../store/slices/tasksSlice';
import { useBreadcrumbs } from '../components/breadcrumbs/BreadcrumbContext';

const Tasks: React.FC = () => {
  useBreadcrumbs([{ label: 'Tasks' }]);
  const { searchTerm, setSearchTerm, getFilteredTasks } = useStore(
    useShallow(state => ({
      searchTerm: state.searchTerm,
      setSearchTerm: state.setSearchTerm,
      getFilteredTasks: state.getFilteredTasks,
    }))
  );

  const filteredTasks = getFilteredTasks();

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'High':
        return 'border-transparent bg-destructive text-destructive-foreground';
      case 'Medium':
        return 'border-transparent bg-secondary text-secondary-foreground';
      case 'Low':
        return 'border-transparent bg-primary text-primary-foreground';
      default:
        return 'border-transparent bg-muted text-muted-foreground';
    }
  };

  const stats = {
    total: filteredTasks.length,
    pending: filteredTasks.filter((task: Task) => task.status === 'Pending')
      .length,
    inProgress: filteredTasks.filter(
      (task: Task) => task.status === 'In Progress'
    ).length,
    completed: filteredTasks.filter((task: Task) => task.status === 'Completed')
      .length,
  };

  return (
    <div className='space-y-6'>
      <div className='flex flex-col gap-3 md:flex-row md:items-center'>
        <button className='inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2'>
          <Plus className='h-4 w-4 mr-2' />
          Create Task
        </button>
      </div>

      {/* Search and Filters */}
      <div className='rounded-lg border bg-card text-card-foreground shadow-sm p-6'>
        <div className='flex flex-col lg:flex-row gap-4'>
          <div className='flex-1 relative'>
            <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground' />
            <input
              type='text'
              placeholder='Search tasks by title, description, or assignee...'
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className='flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-placeholder focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 pl-10'
            />
          </div>
          <select className='flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-placeholder focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50'>
            <option value=''>All Priorities</option>
            <option value='high'>High</option>
            <option value='medium'>Medium</option>
            <option value='low'>Low</option>
          </select>
          <select className='flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-placeholder focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50'>
            <option value=''>All Statuses</option>
            <option value='pending'>Pending</option>
            <option value='in-progress'>In Progress</option>
            <option value='completed'>Completed</option>
          </select>
          <button className='inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2'>
            <Filter className='h-4 w-4 mr-2' />
            Filter
          </button>
        </div>
      </div>

      {/* Tasks Grid */}
      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
        {filteredTasks.map((task: Task) => (
          <div
            key={task.id}
            className='rounded-lg border bg-card text-card-foreground shadow-sm hover:shadow-sm transition-shadow'
          >
            <div className='p-6'>
              <div className='flex justify-between items-start mb-4'>
                <span
                  className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${getPriorityColor(task.priority)}`}
                >
                  {task.priority}
                </span>
                <button className='text-muted-foreground hover:text-foreground'>
                  <MoreHorizontal className='h-4 w-4' />
                </button>
              </div>

              <h3 className='text-lg font-medium mb-2'>{task.title}</h3>
              <p className='text-sm text-muted-foreground mb-4'>
                {task.description}
              </p>

              <div className='space-y-2 mb-4'>
                <div className='flex items-center gap-1 text-sm text-muted-foreground'>
                  <User className='h-3 w-3' />
                  {task.assignedTo}
                </div>
                <div className='flex items-center gap-1 text-sm text-muted-foreground'>
                  <Calendar className='h-3 w-3' />
                  Due: {task.dueDate}
                </div>
                <div className='flex items-center gap-1 text-sm text-muted-foreground'>
                  <Clock className='h-3 w-3' />
                  Created: {task.createdDate}
                </div>
                <div className='flex items-center gap-1 text-sm text-muted-foreground'>
                  <CheckCircle className='h-3 w-3' />
                  {task.status === 'Completed' ? 'Completed' : 'Not completed'}
                </div>
                <div className='text-sm text-muted-foreground'>
                  {task.category}
                </div>
              </div>

              <div className='flex space-x-2'>
                <button className='flex-1 inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-9 px-3'>
                  Complete
                </button>
                <button className='flex-1 inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 px-3'>
                  Edit
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Stats */}
      <div className='grid grid-cols-1 md:grid-cols-4 gap-6'>
        <div className='rounded-lg border bg-card text-card-foreground shadow-sm p-6'>
          <div className='flex items-center'>
            <div className='p-2 bg-primary/10 rounded-lg'>
              <AlertCircle className='h-6 w-6 text-primary' />
            </div>
            <div className='ml-4'>
              <p className='text-sm font-medium text-muted-foreground'>
                Total Tasks
              </p>
              <p className='text-2xl font-semibold'>{stats.total}</p>
            </div>
          </div>
        </div>

        <div className='rounded-lg border bg-card text-card-foreground shadow-sm p-6'>
          <div className='flex items-center'>
            <div className='p-2 bg-secondary/10 rounded-lg'>
              <Clock className='h-6 w-6 text-secondary-foreground' />
            </div>
            <div className='ml-4'>
              <p className='text-sm font-medium text-muted-foreground'>
                Pending
              </p>
              <p className='text-2xl font-semibold'>{stats.pending}</p>
            </div>
          </div>
        </div>

        <div className='rounded-lg border bg-card text-card-foreground shadow-sm p-6'>
          <div className='flex items-center'>
            <div className='p-2 bg-accent/10 rounded-lg'>
              <Clock className='h-6 w-6 text-accent-foreground' />
            </div>
            <div className='ml-4'>
              <p className='text-sm font-medium text-muted-foreground'>
                In Progress
              </p>
              <p className='text-2xl font-semibold'>{stats.inProgress}</p>
            </div>
          </div>
        </div>

        <div className='rounded-lg border bg-card text-card-foreground shadow-sm p-6'>
          <div className='flex items-center'>
            <div className='p-2 bg-primary/10 rounded-lg'>
              <CheckCircle className='h-6 w-6 text-primary' />
            </div>
            <div className='ml-4'>
              <p className='text-sm font-medium text-muted-foreground'>
                Completed
              </p>
              <p className='text-2xl font-semibold'>{stats.completed}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Tasks;
